import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ENDPOINTS } from '@shared/constants/endpoints.constants';
import { ApiResponse } from '@shared/types/api-response.type';
import { UserNotification } from '../../models/entity/user-notification/user-notification.model';

/**
 * The current employee's in-app inbox (the bell). Always scoped to the current user
 * server-side — there is no way to read another user's notifications.
 */
@Injectable({
    providedIn: 'root'
})
export class UserNotificationsService {

    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly httpClient = inject(HttpClient);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    private readonly endpoint = ENDPOINTS.userNotification.base;

    // -----------------------------------------------------------------------------------------------------
    // @ METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * The newest notifications, unread first is the caller's concern — the server returns
     * newest first.
     * @param unreadOnly - Narrow to the unread ones.
     * @param take - Page size (the bell panel shows a handful).
     */
    getAll(unreadOnly = false, take = 30) {
        const params: Record<string, string> = {
            orderBy: '-createdAt',
            take: `${take}`,
        };
        if (unreadOnly) { params['unreadOnly'] = 'true'; }

        return this.httpClient
            .get<ApiResponse<{ items?: Partial<UserNotification>[] } | Partial<UserNotification>[]>>(this.endpoint, { params })
            .pipe(map((response) => {
                const data = response.data;
                const raw = Array.isArray(data) ? data : (data?.items ?? []);

                return raw.map((item) => new UserNotification(item));
            }));
    }

    /** The badge number on the bell. */
    getUnreadCount() {
        return this.httpClient
            .get<ApiResponse<number>>(ENDPOINTS.userNotification.unreadCount)
            .pipe(map((response) => response.data ?? 0));
    }

    /**
     * Marks one notification read (idempotent).
     * @param id - The id of the notification.
     */
    markRead(id: string) {
        return this.httpClient.post<void>(ENDPOINTS.userNotification.read(id), null);
    }

    /** Marks the whole inbox read. */
    markAllRead() {
        return this.httpClient.post<void>(ENDPOINTS.userNotification.readAll, null);
    }

    /**
     * Removes one notification from the inbox.
     * @param id - The id of the notification.
     */
    delete(id: string) {
        return this.httpClient.delete<void>(`${this.endpoint}/${id}`);
    }
}
